import { Component, computed, effect, inject, signal } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucidePlay } from '@ng-icons/lucide';
import {
  BrnAlertDialogImports,
  HlmAlertDialogImports,
  HlmButtonImports,
} from '@pagai-fe/ui';
import { TutorialService } from '../../services/tutorial.service';
import { PageTutorial, TutorialId } from '../../config/tutorials.config';

/**
 * Picker dialog opened from the sidebar "Tutorial" button.
 *
 * Shows the tutorial variants available on the current route. If
 * there is exactly one variant, the user just confirms to start it.
 * If there are several, the user picks one. If none, an empty-state
 * message is shown.
 *
 * The global welcome tour is NOT offered here — that's reached via
 * the sidebar logo.
 */
@Component({
  selector: 'app-tutorial-picker-dialog',
  standalone: true,
  imports: [
    NgIcon,
    HlmAlertDialogImports,
    BrnAlertDialogImports,
    HlmButtonImports,
  ],
  providers: [provideIcons({ lucidePlay })],
  templateUrl: './tutorial-picker-dialog.component.html',
})
export class TutorialPickerDialogComponent {
  private readonly _tutorialService = inject(TutorialService);

  private readonly _isOpen = signal(false);
  protected readonly isOpen = this._isOpen.asReadonly();

  /** Tutorials available on the current route, computed at open time. */
  private readonly _routeVariants = signal<readonly PageTutorial[]>([]);
  protected readonly routeVariants = this._routeVariants.asReadonly();

  /** Resolved dialog title and description (from route config or fallback). */
  protected readonly dialogTitle = signal('Tutorial');
  protected readonly dialogDescription = signal(
    'Scegli la guida interattiva da avviare. Puoi interromperla in qualsiasi momento.',
  );

  protected readonly hasRouteVariants = computed(() => this._routeVariants().length > 0);

  constructor() {
    // Allow in-page entry points (e.g. an icon next to a page title) to
    // open the same picker the sidebar Tutorial button uses, by flipping
    // a signal on TutorialService instead of needing a viewChild ref.
    effect(() => {
      if (this._tutorialService.pickerRequested()) {
        this._tutorialService.acknowledgePicker();
        this.open();
      }
    });
  }

  public open(): void {
    const config = this._tutorialService.resolveAvailableTutorial();
    if (config) {
      this._routeVariants.set(config.tutorials);
      this.dialogTitle.set(config.dialogTitle);
      this.dialogDescription.set(config.dialogDescription);
    } else {
      this._routeVariants.set([]);
      this.dialogTitle.set('Tutorial');
      this.dialogDescription.set(
        'Per questa pagina non sono ancora disponibili tutorial dedicati.',
      );
    }
    this._isOpen.set(true);
  }

  protected close(): void {
    this._isOpen.set(false);
  }

  protected onStateChange(state: 'open' | 'closed'): void {
    if (state === 'closed') this._isOpen.set(false);
  }

  protected runVariant(id: TutorialId): void {
    this.close();
    setTimeout(() => this._tutorialService.start(id), 200);
  }
}
